import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import numeral from 'numeral'
import { Typography, Button, Card, Tag } from 'antd'
import { PlusSquareOutlined, MinusSquareOutlined } from '@ant-design/icons'
import { Brick } from '../../../Components/UI'
import { NUMERAL_FORMAT } from '../../../Constants'
import { formatCost } from '../../../Utilities'
import { useDateTimeDuration } from '../../../Hooks'
import { s } from './style'
import { AllPropsEnquiryDetailsEventFragment } from './EnquiryDetails.generated-types'

const { Text } = Typography

interface CompProps {
  event: AllPropsEnquiryDetailsEventFragment
}

export const EnquiryDetails: React.FC<CompProps> = ({ event }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(!event.proposals.length)

  const {
    name = '',
    budget = 0,
    currency = 'GBP',
    attendees = '',
    location = '',
    spaceFacilities = [],
    cateringNeeds = '',
    additionalNotes = '',
  } = event

  const { durationFullDate, durationRange } = useDateTimeDuration(event)

  return (
    <>
      <Button
        type='link'
        onClick={() => setExpanded(!expanded)}
        style={{ marginLeft: 6, paddingLeft: 0 }}>
        {expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
        <Text style={s.detailsText}>{t('allProposals.enquiryDetails', 'Enquiry details')}</Text>
      </Button>
      {expanded && (
        <Card style={s.enquiryCard}>
          <section style={s.info}>
            {durationFullDate && durationRange && (
              <Brick title={t('enquiry.timings', 'Timings')} data={durationFullDate} small strong>
                <Text style={s.timings}>{durationRange}</Text>
              </Brick>
            )}
            <Brick
              title={t('enquiry.eventName', 'Event name')}
              data={`${name[0].toUpperCase()}${name.substring(1)}`}
              small
            />
            <Brick
              title={t('enquiry.budget', 'Budget')}
              data={formatCost(currency, budget)}
              small
            />
            <Brick
              title={t('enquiry.attendees', 'Attendees')}
              data={numeral(attendees).format(NUMERAL_FORMAT)}
              small
            />
          </section>
          <section style={s.info}>
            <Brick title={t('enquiry.location', 'Preferred location')} data={location} small />
            {spaceFacilities?.length ? (
              <Brick title={t('enquiry.requests', 'Requests')}>
                {spaceFacilities.map(({ name }, i) => (
                  <Tag key={i} style={s.tag}>
                    {name}
                  </Tag>
                ))}
              </Brick>
            ) : null}
          </section>
          <section style={s.info}>
            {additionalNotes?.length && (
              <Brick
                small
                title={t('enquiry.notes', 'Additional notes')}
                data={`'${additionalNotes}'`}
              />
            )}
            {cateringNeeds?.length && (
              <Brick small title={t('enquiry.catering', 'Catering')} data={`'${cateringNeeds}'`} />
            )}
          </section>
        </Card>
      )}
    </>
  )
}
